import { screenFields } from "../../utils/screen";
import React, { useEffect, useState } from "react";

const PKG_ICONS = {
  FOODLINK: "/FoodShare.png",
  BLOODSHARE: "/BloodShare.png",
  BILLBRIDGE: "/BillPayShare.png",
  RESCUEBIDS: "/RescueBids.jpg",
  DIGITHER: "/DigitHer.png",
  EDUCATIONX: "/EducationX.webp",
};

const NEED_OPTIONS = [
  { label: "Food", value: "food", package: "FOODLINK" },
  { label: "Blood", value: "blood", package: "BLOODSHARE" },
  { label: "Bill", value: "billbridge", package: "BILLBRIDGE" },
  { label: "Rescue Bid", value: "rescuebids", package: "RESCUEBIDS" },
  { label: "Digit Her", value: "digither", package: "DIGITHER" },
  { label: "Education", value: "educationx", package: "EDUCATIONX" },
];

const BLOOD_OPTIONS = [
  { label: "A+", value: "A+" }, { label: "A-", value: "A-" },
  { label: "B+", value: "B+" }, { label: "B-", value: "B-" },
  { label: "AB+", value: "AB+" }, { label: "AB-", value: "AB-" },
  { label: "O+", value: "O+" }, { label: "O-", value: "O-" },
];

const DIGITHER_OPTIONS = [
  { label: "Domestic Safety (e.g., domestic violence, stalking)", value: "domestic_safety" },
  { label: "Reproductive Health & Rights", value: "reproductive_health" },
  { label: "Emotional or Mental Health", value: "mental_health" },
  { label: "Financial Support or Independence", value: "financial_support" },
  { label: "Legal Assistance", value: "legal_assistance" },
  { label: "Human Trafficking or Emergency Help", value: "emergency_help" },
  { label: "Professional Growth or Mentorship", value: "professional_growth" },
  { label: "Other", value: "other" },
];

const EDUCATION_SUPPORT = [
  { label: "Tutoring Match", value: "tutoring_match" },
  { label: "Homework Assistant", value: "homework_assistant" },
  { label: "Study Plan Builder", value: "study_plan_builder" },
  { label: "Test Prep Coach", value: "test_prep_coach" },
];

const EDUCATION_SUBJECTS = [
  "Math", "Chemistry", "Physics", "Biology", "English", "History",
  "Geography", "Computer Science", "Economics", "Other",
];

const fieldLabel = { fontSize: 16, fontWeight: 600, color: "#000", marginBottom: 10, display: "block" };
const fieldInput = {
  width: "100%", boxSizing: "border-box", background: "#f0f8ff", border: "none",
  borderRadius: 5, padding: "12px 15px", fontSize: 15, color: "#000",
};
const fieldSelect = { ...fieldInput, height: 50 };
const fieldTextarea = { ...fieldInput, minHeight: 100, resize: "vertical" };
const row = { marginBottom: 20 };

// Blood and food needs have no "by when" date (the client, 17 Oct): the back
// end then uses its default (24 hours).
const NO_BY_WHEN = ["blood", "food"];

function getPackage(needType) {
  return NEED_OPTIONS.find((o) => o.value === needType)?.package || "";
}

export default function PostNeedFormWeb({
  open, onClose, onSubmit, userPackages = [], presetNeedType = "",
}) {
  const [needType, setNeedType] = useState("");
  const [address, setAddress] = useState("");
  const [bloodType, setBloodType] = useState("");
  const [description, setDescription] = useState("");
  const [byWhen, setByWhen] = useState("");
  const [digitHerSupportType, setDigitHerSupportType] = useState("");
  const [educationSupportType, setEducationSupportType] = useState("");
  const [educationSubject, setEducationSubject] = useState("");
  const [educationTopic, setEducationTopic] = useState("");
  const [formError, setFormError] = useState("");
  const [locating, setLocating] = useState(false);
  // The coordinates behind a "Use my current location" address (cleared when the box is edited).
  const [coords, setCoords] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const filteredOptions = NEED_OPTIONS.filter((o) => userPackages.includes(o.package));
  const packagesKey = userPackages.join(",");

  useEffect(() => {
    if (!open) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setByWhen(tomorrow.toISOString().slice(0, 10));
    setAddress("");
    setCoords(null);
    setBloodType("");
    setDescription("");
    setDigitHerSupportType("");
    setEducationSupportType("");
    setEducationSubject("");
    setEducationTopic("");
    setNeedType(presetNeedType || "");
  }, [open, presetNeedType, packagesKey]);

  if (!open) return null;

  const showDigitHerDescription = digitHerSupportType === "other" || !!digitHerSupportType;

  function resetAndClose() {
    onClose();
  }

  // "Use my current location": the browser's position, written into the box.
  function useCurrentLocation() {
    if (!navigator.geolocation) { setFormError("Your browser can't share its location. Please type it."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const point = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        let address = "";
        try {
          const r = await fetch("/api/reverseGeocode", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(point) });
          const j = await r.json().catch(() => ({}));
          if (r.ok && j.status === "OK" && j.address) address = j.address;
        } catch (_) { /* fall back to the coordinates */ }
        setLocating(false);
        setFormError("");
        setAddress(address || `My current location (${point.latitude.toFixed(5)}, ${point.longitude.toFixed(5)})`);
        setCoords(point);
      },
      () => { setLocating(false); setFormError("I couldn't get your location. Please type it."); },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  }

  async function postPayload(payload) {
    setSubmitting(true);
    try {
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePostNeed() {
    if (!needType) return;
    // Free-text boxes get the same prefilter as chat (FIELD_PREFILTER_ENABLED).
    const check = await screenFields([["address", address], ["description", description], ["topic", needType === "educationx" ? educationTopic : ""]]);
    if (!check.ok) { setFormError(check.message); return; }
    setFormError("");

    if (needType === "educationx") {
      const educationDescription = `Needs ${educationSupportType.replace(/_/g, " ")} in ${educationSubject}, ${educationTopic}`;
      await postPayload({
        needType: "educationx",
        address: address.trim(),
        packageName: "EDUCATIONX",
        description: educationDescription,
        byWhen: new Date(`${byWhen}T23:59:59`).toISOString(),
        supportType: educationSupportType,
      });
      resetAndClose();
      return;
    }

    await postPayload({
      needType,
      address: address.trim(),
      packageName: getPackage(needType),
      bloodType: needType === "blood" ? bloodType : undefined,
      description: ["food", "billbridge", "rescuebids", "digither"].includes(needType)
        ? description.trim() || undefined
        : undefined,
      byWhen: NO_BY_WHEN.includes(needType) ? undefined : new Date(`${byWhen}T23:59:59`).toISOString(),
      supportType: needType === "digither" ? digitHerSupportType : undefined,
      location: coords || undefined,
    });
    resetAndClose();
  }

  const postDisabled =
    submitting ||
    !needType ||
    (needType === "blood" && !bloodType) ||
    (needType === "digither" && !digitHerSupportType) ||
    (needType === "educationx" && (!educationSupportType || !educationSubject || !educationTopic));

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9700, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", padding: 10 }}
      onClick={resetAndClose}>
      <div style={{ background: "#FFF", borderRadius: 10, padding: 20, width: "min(520px,100%)", maxHeight: "88vh", overflow: "auto", position: "relative" }}
        onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={resetAndClose}
          style={{ position: "absolute", top: 10, right: 10, border: "none", background: "transparent", fontSize: 22, cursor: "pointer", color: "#666", lineHeight: 1 }}>✕</button>

        <div style={{ fontSize: 18, fontWeight: "bold", color: "#000", textAlign: "center", margin: "20px 0" }}>Post a Need</div>

        <div style={row}>
          <span style={fieldLabel}>Need Type</span>
          <div style={{ display: "flex", alignItems: "center", gap: 12, ...fieldSelect, padding: "8px 12px" }}>
            {needType && PKG_ICONS[getPackage(needType)] && (
              <img src={PKG_ICONS[getPackage(needType)]} alt="" style={{ width: 34, height: 34, objectFit: "cover" }} />
            )}
            <select value={needType} onChange={(e) => {
              setNeedType(e.target.value);
              setBloodType("");
              setDigitHerSupportType("");
              setEducationSupportType("");
              setEducationSubject("");
              setEducationTopic("");
              setDescription("");
            }} style={{ flex: 1, border: "none", background: "transparent", fontSize: 16, color: "#000" }}>
              <option value="">Select Need Type</option>
              {filteredOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {needType === "blood" && (
          <>
            <div style={row}>
              <span style={fieldLabel}>Blood type:</span>
              <select value={bloodType} onChange={(e) => setBloodType(e.target.value)} style={fieldSelect}>
                <option value="">Select Blood Group</option>
                {BLOOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </>
        )}

        {needType === "digither" && (
          <>
            <div style={row}>
              <span style={fieldLabel}>What type of support do you need today?</span>
              <select value={digitHerSupportType} onChange={(e) => setDigitHerSupportType(e.target.value)} style={fieldSelect}>
                <option value="">Select support category</option>
                {DIGITHER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {showDigitHerDescription && (
              <div style={row}>
                <span style={fieldLabel}>
                  {digitHerSupportType === "other"
                    ? "Please describe your safety concern or support needed"
                    : "Additional details (optional)"}
                </span>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder={digitHerSupportType === "other"
                    ? "e.g. I need immediate help due to domestic violence threat"
                    : "Any additional information that might help"}
                  style={fieldTextarea} />
              </div>
            )}
          </>
        )}

        {["food", "billbridge", "rescuebids"].includes(needType) && (
          <div style={row}>
            <span style={fieldLabel}>
              {needType === "food" ? "What kind of food assistance do you need?"
                : needType === "billbridge" ? "What's this bill about?"
                : "Why do you need a RescueBid?"}
            </span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder={
                needType === "food" ? "e.g. Hot meal for 2 people"
                : needType === "billbridge" ? "e.g. Utility bill due in 2 days"
                : "e.g. Need $50 to avoid overdraft fee"
              }
              style={fieldTextarea} />
          </div>
        )}

        {needType === "educationx" && (
          <>
            <div style={row}>
              <span style={fieldLabel}>What type of education support do you need?</span>
              <select value={educationSupportType} onChange={(e) => setEducationSupportType(e.target.value)} style={fieldSelect}>
                <option value="">Select Education Service</option>
                {EDUCATION_SUPPORT.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {educationSupportType && (
              <div style={row}>
                <span style={fieldLabel}>What subject do you need help with?</span>
                <select value={educationSubject} onChange={(e) => setEducationSubject(e.target.value)} style={fieldSelect}>
                  <option value="">Select Subject</option>
                  {EDUCATION_SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            {educationSubject && (
              <div style={row}>
                <span style={fieldLabel}>What specific topic do you need help with?</span>
                <textarea value={educationTopic} onChange={(e) => setEducationTopic(e.target.value)}
                  placeholder="e.g. Linear Algebra, Chemical Bonds, etc." style={fieldTextarea} />
              </div>
            )}
          </>
        )}

        {!NO_BY_WHEN.includes(needType) && <div style={row}>
          <span style={fieldLabel}>By when do you need it?</span>
          <div style={{ ...fieldSelect, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <input type="date" required value={byWhen} min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setByWhen(e.target.value)}
              style={{ flex: 1, border: "none", background: "transparent", fontSize: 15, color: "#000" }} />
            <span style={{ fontSize: 18, opacity: 0.5 }}>📅</span>
          </div>
        </div>}

        <div style={row}>
          <span style={fieldLabel}>Location</span>
          <textarea value={address} onChange={(e) => { setAddress(e.target.value); setCoords(null); }}
            placeholder="Enter a location" required aria-label="Location" style={fieldTextarea} />
          <button type="button" onClick={useCurrentLocation} disabled={locating}
            style={{ marginTop: 8, border: "1px solid #9cc4e4", background: "#fff", color: "#1d3b53", borderRadius: 8, padding: "8px 12px", fontSize: 14, cursor: locating ? "default" : "pointer" }}>
            {locating ? "Finding you…" : "📍 Use my current location"}
          </button>
        </div>

        {formError && <div role="alert" style={{ color: "#c00", fontSize: 13, margin: "8px 0" }}>{formError}</div>}
        <button type="button" onClick={handlePostNeed} disabled={postDisabled}
          style={{
            width: 150, background: postDisabled ? "#ccc" : "green", padding: "15px 0",
            borderRadius: 10, alignSelf: "center", display: "block", margin: "10px auto 0",
            border: "none", color: "#fff", fontWeight: "bold", fontSize: 16, cursor: postDisabled ? "default" : "pointer",
          }}>
          {submitting ? "Posting…" : "Post Need"}
        </button>
      </div>
    </div>
  );
}
